import { useIntl } from "react-intl";
import clsx from "clsx";
import { Message } from "../../BP2Components";
import "./questionnaire.less";

const QuestionnaireStepper = ({
   tabKeys,
   rawTabs,
   activeTabId,
   setActiveTabId,
   tabCompletionStatus,
   visitedTabKeys,
   markTabVisited,
   currentPageIndex,
   goToPrev,
   goToNext,
   handleSave,
   validateCurrentPage,
   requiredFieldIds,
}) => {
   const intl = useIntl();
   const totalSteps = tabKeys.length;
   const isFirstPage = currentPageIndex === 0;
   const isLastPage = currentPageIndex === totalSteps - 1;
   const currentLabel = rawTabs[activeTabId]?.label?.trim();

   return (
      <div className="q-pagination">
         {/* Progress Header */}
         <div className="q-pagination__header">
            <div className="q-pagination__meta">
               <span className="q-pagination__step-indicator">
                  {currentPageIndex + 1}
                  <span className="q-pagination__step-separator">/</span>
                  {totalSteps}
               </span>
               {currentLabel && <span className="q-pagination__current-label">{currentLabel}</span>}
            </div>
         </div>

         {/* Segmented Progress Track */}
         <div className="q-pagination__track">
            {tabKeys.map((key, index) => {
               const isActive = activeTabId === key;
               const isComplete = tabCompletionStatus?.[key] === true;
               const isError = tabCompletionStatus?.[key] === false && visitedTabKeys?.has(key);
               return (
                  <button
                     type="button"
                     key={key}
                     className={clsx(
                        "q-pagination__segment",
                        isActive && "q-pagination__segment--active",
                        isError && "q-pagination__segment--error",
                        isComplete && "q-pagination__segment--complete",
                     )}
                     onClick={() => {
                        if (key !== activeTabId) markTabVisited(activeTabId);
                        setActiveTabId(key);
                     }}
                     aria-label={rawTabs[key]?.label?.trim() || String(index + 1)}
                     aria-current={isActive ? "step" : undefined}
                  />
               );
            })}
         </div>

         {/* Navigation */}
         <div className="q-pagination__nav">
            <button
               type="button"
               className={clsx(
                  "q-pagination__btn",
                  "q-pagination__btn--prev",
                  isFirstPage && "q-pagination__btn--disabled",
               )}
               onClick={goToPrev}
               disabled={isFirstPage}
               aria-label={intl.formatMessage({ id: "settings.previous" })}
            >
               <svg
                  className="q-pagination__btn-icon"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
               >
                  <path
                     d="M12.5 15L7.5 10L12.5 5"
                     stroke="currentColor"
                     strokeWidth="1.5"
                     strokeLinecap="round"
                     strokeLinejoin="round"
                  />
               </svg>
               <span className="q-pagination__btn-text">
                  {intl.formatMessage({ id: "settings.previous" })}
               </span>
            </button>

            {isLastPage ? (
               <button
                  type="button"
                  className="q-pagination__btn q-pagination__btn--submit"
                  onClick={async () => {
                     const validationResult = await validateCurrentPage(requiredFieldIds);
                     const isAllComplete = Object.values(tabCompletionStatus ?? {}).every(Boolean);
                     if (!isAllComplete || validationResult.hasValidationErrors) {
                        Message.warning(
                           intl.formatMessage({
                              id: "questionnaire.incompleteWarning",
                              defaultMessage: "Wypełnij wszystkie wymagane pola przed zapisaniem.",
                           }),
                        );
                        return;
                     }
                     handleSave(null, requiredFieldIds, validationResult);
                  }}
               >
                  <span className="q-pagination__btn-text">
                     {intl.formatMessage({ id: "global.save" })}
                  </span>
                  <svg
                     className="q-pagination__btn-icon"
                     viewBox="0 0 20 20"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M16.667 5L7.5 14.167L3.333 10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
               </button>
            ) : (
               <button
                  type="button"
                  className="q-pagination__btn q-pagination__btn--next"
                  onClick={goToNext}
               >
                  <span className="q-pagination__btn-text">
                     {intl.formatMessage({ id: "settings.next" })}
                  </span>
                  <svg
                     className="q-pagination__btn-icon"
                     viewBox="0 0 20 20"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M7.5 5L12.5 10L7.5 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
               </button>
            )}
         </div>
      </div>
   );
};

export default QuestionnaireStepper;
